import { Link } from "react-router-dom";
import { Shield, Sparkles, Lock, Users, MessageSquare, Headphones } from "lucide-react";
import { LOGO_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Home() {
  const { user } = useAuth();
  return (
    <div className="space-y-6" data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FFFFFF] via-[#F5F8FD] to-[#EEF3FA] border border-slate-200 rounded-3xl p-6 sm:p-10 lg:p-12">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#1D5BFF]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#4DBFFF]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="relative grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#4DBFFF] mb-3">
              <Sparkles className="w-3 h-3" /> Community
            </div>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tighter leading-[1.05]">
              Tausche dich live aus –<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1D5BFF] to-[#4DBFFF]">
                mit der TSU-Family.
              </span>
            </h1>
            <p className="text-sm sm:text-base text-slate-700 mt-4 max-w-xl">
              Fahrer aus Deutschland, Österreich und der Schweiz tauschen sich gerade aus – über Strecken, Erfahrungen und Tipps.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/chat"
                data-testid="hero-cta-chat"
                className="btn-brand inline-flex items-center gap-2 font-bold px-6 py-3 rounded-full"
              >
                <MessageSquare className="w-4 h-4" /> Zum Chat
              </Link>
              <Link
                to="/mitarbeiter"
                data-testid="hero-cta-support"
                className="inline-flex items-center gap-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-400 text-emerald-700 hover:text-emerald-900 font-bold px-6 py-3 rounded-full transition-all active:scale-95"
              >
                <Headphones className="w-4 h-4" /> Support kontaktieren
              </Link>
              {!user && (
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 bg-slate-100/70 hover:bg-slate-100 border border-slate-200 text-slate-900 font-bold px-6 py-3 rounded-full"
                >
                  Jetzt beitreten
                </Link>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-6">
              <Feature icon={Lock} title="Freischaltung" sub="Admin-Approval" />
              <Feature icon={Sparkles} title="Tipps" sub="Von Profis" />
              <Feature icon={Shield} title="Geprüft" sub="Nur TSU-Partner" />
            </div>
          </div>
          <div className="hidden lg:flex justify-center">
            <div className="relative">
              <div className="bg-[#F5F8FD] border-[10px] border-[#1D5BFF] rounded-[40px] p-2 shadow-2xl w-72">
                <div className="rounded-[28px] bg-gradient-to-br from-[#FFFFFF] to-[#F5F8FD] p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <img src={LOGO_URL} alt="TSU" className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <div className="text-xs font-bold text-slate-900">TSU Community</div>
                      <div className="text-[10px] text-emerald-400">● online</div>
                    </div>
                  </div>
                  <MockMsg side="left" text="Hat schon jemand die Strecke Berlin → München gemacht?" />
                  <MockMsg side="left" text="Ja gestern! Lief super, Auszahlung schon da 💸" />
                  <MockMsg side="left" text="BMW M3 — was für ein Auto 🔥" />
                  <MockMsg side="right" text="Mega! 🚀" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <QuickLink to="/chat" label="Chat" sub="Community" icon={MessageSquare} />
        <QuickLink to="/mitarbeiter" label="Mitarbeiter" sub="TSU-Team" icon={Headphones} />
        <QuickLink to="/live" label="Live" sub="Talk & Q&A" icon={Sparkles} />
        <QuickLink to="/verlosung" label="Lose" sub="Gewinne" icon={Sparkles} />
        <QuickLink to="/rangliste" label="Rangliste" sub="Top Fahrer" icon={Users} />
      </section>
    </div>
  );
}

function Feature({ icon: Icon, title, sub }) {
  return (
    <div className="bg-[#FFFFFF]/60 border border-slate-200/60 rounded-xl p-3">
      <Icon className="w-4 h-4 text-[#4DBFFF] mb-1" />
      <div className="text-xs font-bold text-slate-900">{title}</div>
      <div className="text-[10px] text-slate-500">{sub}</div>
    </div>
  );
}

function MockMsg({ side, text }) {
  if (side === "right")
    return (
      <div className="flex justify-end">
        <div className="bg-[#1D5BFF] text-white text-[11px] px-3 py-1.5 rounded-2xl rounded-tr-md max-w-[80%]">{text}</div>
      </div>
    );
  return (
    <div className="flex">
      <div className="bg-[#EEF3FA] text-slate-800 text-[11px] px-3 py-1.5 rounded-2xl rounded-tl-md max-w-[80%]">{text}</div>
    </div>
  );
}

function QuickLink({ to, label, sub, icon: Icon }) {
  return (
    <Link
      to={to}
      data-testid={`quick-link-${to.slice(1)}`}
      className="group bg-[#FFFFFF] border border-slate-200 rounded-2xl p-4 hover:border-[#1D5BFF]/50 hover:shadow-[0_8px_30px_rgba(0,102,255,0.15)] transition-all"
    >
      <Icon className="w-5 h-5 text-[#4DBFFF] mb-2 group-hover:scale-110 transition-transform" />
      <div className="text-slate-900 font-bold text-sm">{label}</div>
      <div className="text-[10px] text-slate-500">{sub}</div>
    </Link>
  );
}
